import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Minus, Plus, ShoppingCart, AlertTriangle, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart, tierFor, unitPriceCents } from "@/context/CartContext";
import { toast } from "sonner";
import { API } from "@/lib/api";
import ResponsiveImage from "@/components/ResponsiveImage";

const SPECS = [
  ["Composition", "Sterile water, 0.9% benzyl alcohol"],
  ["Grade", "Research reagent"],
  ["Packaging", "Box of 10 sealed glass vials"],
  ["Labelling", "None — vials ship unlabeled"],
  ["Storage", "Ambient, protect from light"],
];

function ProductCard({ product, index, lot }) {
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);

  const handleAdd = () => {
    addItem(product, qty);
    toast.success(`${product.name} (${product.size}) added to cart`, {
      description: "For research purposes only.",
    });
    setQty(1);
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 48 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: index * 0.12 }}
      className="border border-slate-200 bg-white group"
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative overflow-hidden bg-slate-50 border-b border-slate-200">
        <ResponsiveImage
          src={product.image}
          alt={`Lab2o bacteriostatic water ${product.size} sterile research vial, unlabeled`}
          width={1408}
          height={768}
          className="w-full aspect-[4/3] object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
          loading="lazy"
          dataTestid={`product-image-${product.id}`}
        />
        <span className="absolute top-4 left-4 bg-white border border-slate-200 px-3 py-1 font-plexmono text-[10px] tracking-[0.2em] uppercase text-slate-500">
          {product.sku}
        </span>
      </div>

      <div className="p-7 sm:p-9">
        <div className="flex items-baseline justify-between gap-4">
          <h3 className="font-chivo font-extrabold tracking-tight text-2xl text-slate-950" data-testid={`product-name-${product.id}`}>
            {product.name}
          </h3>
          <span className="font-chivo font-black text-3xl text-slate-950 shrink-0" data-testid={`product-size-${product.id}`}>
            {product.size}
          </span>
        </div>

        <p className="mt-3 text-sm text-slate-600 leading-relaxed" data-testid={`product-description-${product.id}`}>
          {product.description}
        </p>

        <dl className="mt-6 border-t border-slate-100">
          {SPECS.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4 py-2.5 border-b border-slate-100">
              <dt className="font-plexmono text-[10px] tracking-[0.18em] uppercase text-slate-400 pt-0.5">{k}</dt>
              <dd className="text-xs text-slate-700 text-right">{v}</dd>
            </div>
          ))}
        </dl>

        <div className="mt-6 border border-slate-200 bg-slate-50 px-4 py-3" data-testid={`product-bulk-tiers-${product.id}`}>
          <p className="font-plexmono text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-1.5">Bulk pricing</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {[...(product.tiers || [])].sort((a, b) => b.min - a.min).map((t) => (
              <span key={t.min} className="text-xs text-slate-600">
                {t.min}+ {product.sell_unit === "single" ? "units" : "boxes"} <span className="font-semibold text-slate-950">−{t.discount_pct}%</span>
              </span>
            ))}
          </div>
        </div>

        <div className="mt-7 flex items-center justify-between">
          <div>
            <span className="font-chivo font-extrabold text-2xl text-slate-950" data-testid={`product-price-${product.id}`}>
              ${(unitPriceCents(product.priceCents, qty, product.tiers) / 100).toFixed(2)}
            </span>
            {tierFor(qty, product.tiers) && (
              <span className="ml-2 text-sm text-slate-400 line-through" data-testid={`product-base-price-${product.id}`}>
                ${(product.priceCents / 100).toFixed(2)}
              </span>
            )}
            <span className="block font-plexmono text-[10px] tracking-[0.15em] uppercase text-slate-400 mt-1">
              {product.sell_unit === "single" ? "per vial" : "per box of 10"}
            </span>
          </div>
          <div className="flex items-center border border-slate-300" data-testid={`product-qty-selector-${product.id}`}>
            <button
              onClick={() => setQty((q) => Math.max(1, q - 1))}
              className="p-2.5 hover:bg-slate-100 transition-colors"
              aria-label="Decrease quantity"
              data-testid={`product-qty-minus-${product.id}`}
            >
              <Minus className="w-3.5 h-3.5" />
            </button>
            <span className="w-10 text-center font-plexmono text-sm" data-testid={`product-qty-value-${product.id}`}>{qty}</span>
            <button
              onClick={() => setQty((q) => Math.min(99, q + 1))}
              className="p-2.5 hover:bg-slate-100 transition-colors"
              aria-label="Increase quantity"
              data-testid={`product-qty-plus-${product.id}`}
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {lot && (
          <div className="mt-6 border border-slate-200 bg-white px-4 py-3 flex items-center justify-between gap-3" data-testid={`product-current-lot-${product.id}`}>
            <div>
              <p className="font-plexmono text-[10px] tracking-[0.18em] uppercase text-slate-400">Current lot</p>
              <p className="font-plexmono text-sm text-slate-950 mt-0.5" data-testid={`product-lot-number-${product.id}`}>{lot.lot_number}</p>
              {lot.report_date && (
                <p className="text-[11px] text-slate-500 mt-0.5">Tested {lot.report_date}</p>
              )}
            </div>
            {lot.coa_pdf_url && (
              <a
                href={lot.coa_pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 border border-slate-950 px-3 py-2 font-plexmono text-[10px] tracking-[0.12em] uppercase hover:bg-slate-950 hover:text-white transition-colors shrink-0"
                data-testid={`product-view-coa-${product.id}`}
              >
                <FileText className="w-3.5 h-3.5" /> View COA
              </a>
            )}
          </div>
        )}

        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={handleAdd}
          className="mt-5 w-full bg-slate-950 text-white py-4 font-plexmono text-xs tracking-[0.2em] uppercase hover:bg-slate-800 transition-colors flex items-center justify-center gap-3"
          data-testid={`product-add-to-cart-${product.id}`}
        >
          <ShoppingCart className="w-3.5 h-3.5" />
          Add to Cart
        </motion.button>

        <p className="mt-4 flex items-start gap-2 text-red-700 text-xs font-medium leading-snug" data-testid={`product-disclaimer-${product.id}`}>
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          For research purposes only. Not for human or veterinary use or consumption.
        </p>
      </div>
    </motion.article>
  );
}

export default function Products() {
  const { products, productsLoaded } = useCart();
  const [lotsBySku, setLotsBySku] = useState({});

  useEffect(() => {
    axios
      .get(`${API}/lots`)
      .then(({ data }) => {
        const map = {};
        (data.lots || [])
          .filter((l) => l.status === "Current")
          .forEach((l) => {
            // lots already sorted newest-first by report_date on the backend
            if (!map[l.product_sku]) map[l.product_sku] = l;
          });
        setLotsBySku(map);
      })
      .catch(() => {});
  }, []);

  return (
    <section id="products" className="py-24 sm:py-32 bg-white" data-testid="products-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        {!productsLoaded && (
          <p className="text-center text-slate-400 font-plexmono text-xs tracking-[0.2em] uppercase py-16" data-testid="products-loading">
            Loading catalogue…
          </p>
        )}
        {productsLoaded && products.length === 0 && (
          <p className="text-center text-slate-500 text-sm py-16" data-testid="products-unavailable">
            The catalogue is temporarily unavailable. Please send us an enquiry below.
          </p>
        )}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-10">
          {products.map((p, i) => (
            <ProductCard
              key={p.product_id}
              product={{ ...p, id: p.product_id, priceCents: p.base_price_cents, tiers: p.bulk_tiers }}
              index={i}
              lot={lotsBySku[p.sku]}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
